/**
 * Calculates the winding number for a given point relative to a polygon's vertices.
 * The winding number is used to determine if a point is inside a polygon.
 * 
 * @param {THREE.Vector2} point - The point to test.
 * @param {Array} vertices - The vertices of the polygon.
 * @returns {number} The winding number: >0 if inside, 0 if outside.
 */
function windingNumber(point, vertices) {
    let wn = 0; // Winding number counter

    // Loop through each edge of the polygon
    for (let i = 0; i < vertices.length; i++) {
        const v1 = vertices[i]; // Start point of the edge
        const v2 = vertices[(i + 1) % vertices.length]; // End point of the edge (wrap around to the first vertex)

        // Check if the point is between the Y values of the edge's endpoints
        if (v1.y <= point.y) {
            // If the point is below or at v1, check if the edge goes above the point
            if (v2.y > point.y && isLeft(v1, v2, point) > 0) {
                wn++; // Point is to the left of the edge, increment the winding number
            }
        } else {
            // If the point is above v1, check if the edge goes below the point
            if (v2.y <= point.y && isLeft(v1, v2, point) < 0) {
                wn--; // Point is to the right of the edge, decrement the winding number
            }
        }
    }
    return wn; // Return the winding number, >0 means inside, 0 means outside
}

/**
 * Helper function to determine if a point is to the left, on, or right of an infinite line.
 * This is based on the cross product of the vectors p0->p1 and p0->p2.
 * 
 * @param {THREE.Vector2} p0 - The starting point of the line.
 * @param {THREE.Vector2} p1 - The ending point of the line.
 * @param {THREE.Vector2} p2 - The point to test.
 * @returns {number} Positive if point is left of the line, 0 if on the line, negative if right.
 */
function isLeft(p0, p1, p2) {
    return (p1.x - p0.x) * (p2.y - p0.y) - (p2.x - p0.x) * (p1.y - p0.y);
}

/**
 * Checks if one polygon (inner) is completely inside another polygon (outer).
 * Uses the winding number algorithm to test if each vertex of the inner polygon is inside the outer polygon.
 * 
 * @param {Array} innerVertices - The vertices of the inner polygon.
 * @param {Array} outerVertices - The vertices of the outer polygon.
 * @returns {boolean} True if the inner polygon is inside the outer polygon, false otherwise.
 */
export function isPolygonInsidePolygon(innerVertices, outerVertices) {
    // Check if every vertex of the inner polygon is inside the outer polygon
    for (const vertex of innerVertices) {
        // If any vertex of the inner polygon is outside the outer polygon (winding number = 0), return false
        if (windingNumber(vertex, outerVertices) === 0) {
            return false;
        }
    }
    return true; // All vertices are inside the outer polygon
}
